import { useGoogleLogin } from '@react-oauth/google';
import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAnalytics, Events, useQueryParams } from '@hooks';
import { getOrCreateSessionId, setSessionUserId } from '../utils/sentryTrackingConfig';
import { setLogoutEventsSent } from '../utils/logoutTrackingGuard';
import { useAuthToken } from './useAuthToken';
import { logger } from '../utils/logger';
import { persistIdpV2Tokens, socialLoginWithIdpV2 } from '../utils/idpV2Auth';

export default function useGoogleAuth(type: 'Login' | 'Registration') {
  const [googleAuthLoading, setGoogleAuthLoading] = useState(false)
  const { setAuthToken, removeAuthToken } = useAuthToken()
  const { push } = useHistory();
  const params = useQueryParams();
  const returnUrl = params.get('returnUrl');
  const { search } = useLocation();
  const [googleAuthUser, setGoogleUser] = useState<{ email: string; given_name: string; family_name: string }>()
  const [error, setError] = useState<Error>()

  const { identify, trackEvent, segment } = useAnalytics();

  const googleAuth = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGoogleAuthLoading(true)
      setError(undefined)
      try {
        if (type === 'Login') {
          const socialLogin = await socialLoginWithIdpV2('Google', tokenResponse.access_token);
          if (socialLogin.access_token) {
            setLogoutEventsSent(false);
            removeAuthToken();
            persistIdpV2Tokens(socialLogin);
            setAuthToken(socialLogin.access_token, true);
            if (socialLogin.user_id) {
              segment?.identify(socialLogin.user_id);
              identify(socialLogin.user_id, {
                email: socialLogin.email,
              });
            }
            const sessionId = getOrCreateSessionId();
            const timestamp = new Date().toISOString();
            const d = { email: socialLogin.email, loginType: 'Google', userId: socialLogin.user_id, sessionId, timestamp };
            trackEvent(Events.actions.logIn, d);
            trackEvent(Events.actions.sessionStarted, d);
            if (socialLogin.user_id) setSessionUserId(socialLogin.user_id);
            if (returnUrl && search.replace('?returnUrl=', '').includes(returnUrl)) {
              const sharePath = search.replace('?returnUrl=', '').replace(`${returnUrl}&`, `${returnUrl}?`)
              push(sharePath);
            } else {
              push('/');
            }
          } else if (socialLogin.mfa_required && socialLogin.challenge_id && (socialLogin.method === 'totp' || socialLogin.method === 'totp_setup')) {
            const state: any = { idpChallengeId: socialLogin.challenge_id, idpMethod: socialLogin.method, email: socialLogin.email }
            if (socialLogin.method === 'totp_setup' && socialLogin.uri) {
              state.totpUri = socialLogin.uri
            }
            push('/verify', state);
          } else if (socialLogin.mfa_required && socialLogin.challenge_id && socialLogin.method === 'email_otp') {
            push('/admin/verify', { idpChallengeId: socialLogin.challenge_id, idpMethod: socialLogin.method, email: socialLogin.email });
          } else {
            throw new Error('Login attempt failed');
          }
        } else {
          const response = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
            method: 'GET',
            headers: { Authorization: `Bearer ${tokenResponse.access_token}` }
          });
          const userInfo = await response.json();
          if (userInfo.email) {
            setGoogleUser({
              email: userInfo.email,
              family_name: userInfo.family_name ?? '',
              given_name: userInfo.given_name ?? ''
            })
          }
        }
      } catch (error) {
        const loginError = error instanceof Error ? error : new Error('Login attempt failed');
        setError(loginError);
        trackEvent(Events.actions.logInFailed, {
          loginType: 'Google',
          clientId: localStorage.getItem('clientId'),
          message: "Login attempt failed",
          error: loginError.message,
        });
        logger.error(error);
      } finally {
        setGoogleAuthLoading(false)
      }
    },
    onError: errorResponse => logger.info(errorResponse),
  });

  return {
    googleAuth,
    googleAuthUser,
    googleAuthLoading,
    googleAuthError: error,
    setGoogleUser
  }
}
